import {gql} from '@apollo/client';

/**
 * Aggregated order metrics for the Trading View.
 *
 * ⚠️ `getOrdersMetrics` — PLURAL. There is an older `getOrderMetrics`
 * (singular) that powers pages/financials/OrderMetrics.js; it is a different
 * query with a different shape and is deliberately left alone.
 *
 * Scope is a filter, not a dimension: every field on `OrdersMetricsInput` is
 * optional and they compose, so one component parameterised by input serves
 * the venue view, the broker view and the platform total.
 */
export const GET_ORDERS_METRICS = gql`
  query OrdersMetrics($input: OrdersMetricsInput) {
    getOrdersMetrics(input: $input) {
      ... on Error {
        message
      }
      ... on ResponseWithOrdersMetrics {
        data {
          generatedAt
          orderCount

          # The filter the server actually applied, echoed back. Read this
          # rather than assuming the input was honoured — a default window may
          # be applied when no dates are sent.
          window {
            startDate
            endDate
            dateField
            stockExchange
            broker
            brokerMatch
            instrumentKey
            assetClass
            direction
            type
            assetCurrency
            includeDerivatives
            notes
          }

          flow {
            pending {
              ...OrderStageFields
            }
            everFilled {
              ...OrderStageFields
            }
            settled {
              ...OrderStageFields
            }
            inFlight {
              ...OrderStageFields
            }
            partiallyFilledOrderCount
            imbalance {
              currency
              amount
            }
            turnover {
              currency
              amount
            }
          }

          fees {
            quoted {
              currency
              amount
            }
            charged {
              currency
              amount
            }
            waived {
              currency
              amount
            }
            waivedOrderCount
            floatingReserveHeld {
              currency
              amount
            }
            returnedToWallet {
              currency
              amount
            }
            byType {
              feeType
              quoted {
                currency
                amount
              }
              charged {
                currency
                amount
              }
            }
            effectiveRateBps
          }

          latency {
            timeToFill {
              count
              averageMs
              minMs
              maxMs
            }
            timeToSettle {
              count
              averageMs
              minMs
              maxMs
            }
            fillToSettle {
              count
              averageMs
              maxMs
            }
          }

          settlement {
            aging {
              underOneDay
              oneToThreeDays
              threeToSevenDays
              sevenToThirtyDays
              overThirtyDays
            }
            oldestUnsettledMs
          }

          quality {
            fillRate
            requestedQuantity
            filledQuantity
            underFilledOrderCount
            shortfallQuantity
            completeFillOrderCount
            slippage {
              currency
              amount
            }
            marketOrderCount
            limitOrderCount
          }

          failures {
            rejectedOrderCount
            cancelledOrderCount
            expiredOrderCount
            erroredOrderCount
            retriedOrderCount
            onHoldOrderCount
            reDispatchedOrderCount
          }

          participation {
            traderCount
            instrumentCount
            brokerCount
            recurringInvestmentOrderCount
            affiliateOrderCount
            collectionOrderCount
            bundleOrderCount
            rewardOrderCount
          }
        }
      }
    }
  }

  fragment OrderStageFields on OrderFlowStage {
    orderCount
    buyOrderCount
    sellOrderCount
    quantity
    notional {
      currency
      amount
    }
    buyNotional {
      currency
      amount
    }
    sellNotional {
      currency
      amount
    }
  }
`;
